import { useEffect, useState } from 'react';
import Carousel from '../components/Carousel';
import coachFallback from '../content/coaches/coach-fallback.png';

type Coach = {
  slug: string;
  name: string;
  rank: string;
  team: string;
  experience: string;
  headline?: string;
  story?: string[];
  achievements?: string[];
  order?: number;
};

function sortRoster(list: Coach[]): Coach[] {
  return (list || []).slice().sort((a, b) => {
    const ao = typeof a.order === 'number' ? a.order : 9999;
    const bo = typeof b.order === 'number' ? b.order : 9999;
    if (ao !== bo) return ao - bo;
    return (a.name || '').localeCompare(b.name || '');
  });
}

function getSummary(c: Coach) {
  if (c.headline && c.headline.trim()) return c.headline.trim();
  const text = Array.isArray(c.story) ? c.story.join(' ') : '';
  if (text) {
    const sentences = text.match(/[^.!?]+[.!?]/g) || [];
    const firstTwo = sentences.slice(0, 2).join(' ').trim();
    return firstTwo || text.slice(0, 140) + (text.length > 140 ? '…' : '');
  }
  return '';
}

export default function Coaches() {
  const [roster, setRoster] = useState<Coach[]>([]);
  const [imagesBySlug, setImagesBySlug] = useState<Record<string, string[]>>({});
  const [openStory, setOpenStory] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    const base = process.env.PUBLIC_URL || '';
    // 1) Discover coach files from index, then fetch individual meta files
    fetch(base + '/coaches-meta/index.json', { signal: controller.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then(async (idx) => {
        if (!idx || !Array.isArray(idx.coaches)) return setRoster([]);
        const list: Coach[] = [];
        for (const it of idx.coaches) {
          try {
            const res = await fetch(base + `/coaches-meta/${it.slug}.json`, { signal: controller.signal });
            if (!res.ok) continue;
            const meta = (await res.json()) as Coach;
            list.push(meta);
          } catch {}
        }
        setRoster(sortRoster(list));
      })
      .catch(() => setRoster([]));

    // 2) Load images mapping from generated public/coaches.json
    fetch(base + '/coaches.json', { signal: controller.signal })
      .then((r) => (r && r.ok ? r.json() : Promise.resolve({})))
      .then((j) => setImagesBySlug(j || {}))
      .catch(() => setImagesBySlug({}));

    return () => controller.abort();
  }, []);

  const coachBySlug = (slug: string) => roster.find((c) => c.slug === slug);

  return (
    <main id="main">
      <section className="section">
        <div className="container coaches-grid">
          <h1 className="section-title">Coaches</h1>
          {roster.map((c) => {
            const imgs = imagesBySlug[c.slug] || [];
            const images = imgs.length ? imgs.map((p) => p.replace(/^\//, '')) : [coachFallback];
            return (
              <article key={c.slug} className="coach-card">
                <div className="coach-media-top">
                  <Carousel images={images} interval={7000} fallback={coachFallback} />
                </div>
                <div className="coach-body">
                  <h2 className="coach-name">{c.name}</h2>
                  <p className="coach-meta">
                    <strong>
                      {c.rank} - {c.team}
                    </strong>{' '}
                    • <strong>{c.experience} experience</strong>
                  </p>
                  {getSummary(c) && <p className="coach-blurb">{getSummary(c)}</p>}
                  <button className="btn btn-outline" onClick={() => setOpenStory(c.slug)}>
                    Read story
                  </button>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      {openStory && (() => {
        const c = coachBySlug(openStory!);
        const imgs = imagesBySlug[c?.slug || ''] || [];
        const images = imgs.length ? imgs.map((p) => p.replace(/^\//, '')) : [coachFallback];
        return (
          // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions, jsx-a11y/click-events-have-key-events
          <div
            className="lightbox-backdrop"
            role="dialog"
            aria-modal="true"
            aria-label={`${c?.name} story`}
            tabIndex={-1}
            onClick={() => setOpenStory(null)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') setOpenStory(null);
            }}
          >
            {/* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions */}
            <div
              className="lightbox-panel story-panel"
              role="document"
              tabIndex={-1}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
            >
              <button className="lightbox-close" aria-label="Close" onClick={() => setOpenStory(null)}>
                x
              </button>
              <div className="story-header">
                <div className="story-media">
                  <Carousel images={images} interval={7000} fallback={coachFallback} />
                </div>
                <div>
                  <h2 style={{ margin: '6px 0' }}>{c?.name}</h2>
                  <p className="coach-meta">
                    <strong>
                      {c?.rank} - {c?.team}
                    </strong>{' '}
                    • <strong>{c?.experience} experience</strong>
                  </p>
                </div>
              </div>
              <div className="story-scroll">
                {c?.story && c.story.length ? (
                  c.story.map((para, i) => <p key={i}>{para}</p>)
                ) : (
                  <p>Bio coming soon. Check back for this coach’s story.</p>
                )}
                {(c?.achievements && c.achievements.length) && (
                  <>
                    <h3>Achievements</h3>
                    <ul>
                      {c.achievements.map((a, idx) => (
                        <li key={idx}>{a}</li>
                      ))}
                    </ul>
                  </>
                )}
              </div>
            </div>
          </div>
        );
      })()}
    </main>
  );
}
